import React, { useId } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '@/components/app';
import { VariantFormData } from '../types';

interface VariantDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: VariantFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<VariantFormData>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  isEditing: boolean;
}

export function VariantDialog({
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  isSubmitting,
  isEditing,
}: VariantDialogProps) {
  const ids = useId().replace(/:/g, '');

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-0 p-0">
        <DialogHeader bordered>
          <DialogTitle>{isEditing ? 'Изменить вариант' : 'Новый ценовой вариант'}</DialogTitle>
          <DialogDescription>Вариант — это весь прайс-лист целиком: и цены, и состав тарифов.</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <FormField label="Название" required>
            <Input
              value={formData.name}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Дешёвый / Контроль / Дорогой"
            />
          </FormField>

          <FormField
            label="Вес набора"
            hint="Не проценты, а вес: 40 / 40 / 20 даст доли 40%, 40% и 20%. Вес 0 — вариант в набор не попадёт."
          >
            <Input
              type="number"
              min={0}
              value={formData.weight}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, weight: parseInt(e.target.value) || 0 }))}
              className="tabular-nums"
            />
          </FormField>

          <FormField label="Описание">
            <Textarea
              value={formData.description}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Гипотеза и планируемая дата остановки — автозавершения нет, тест останавливают вручную"
              rows={3}
            />
          </FormField>

          <div className="flex items-start justify-between gap-3 rounded-xl border border-border-soft bg-surface-alt px-3 py-2.5">
            <span className="flex flex-col gap-0.5">
              <span id={`${ids}-control`} className="text-sm font-semibold">
                Контрольный вариант
              </span>
              <span id={`${ids}-control-hint`} className="text-xs text-text-2">
                Текущие цены. На него переводят назначенных при архивировании других вариантов, поэтому контрольный —
                ровно один.
              </span>
            </span>
            <Switch
              aria-labelledby={`${ids}-control`}
              aria-describedby={`${ids}-control-hint`}
              checked={formData.is_control}
              onCheckedChange={(checked) => onFormDataChange((prev) => ({ ...prev, is_control: checked }))}
            />
          </div>
        </DialogBody>

        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {isEditing ? 'Сохранить' : 'Создать вариант'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
